import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useReveal } from '../components/useReveal';
import emailjs from '@emailjs/browser';

/*
  ✅ EMAILJS SETUP (5 minutes, totally free):
  ─────────────────────────────────────────
  1. Go to https://emailjs.com → Sign up free
  2. Dashboard → Add New Service → choose Gmail (or any email)
     → Authorize your Gmail → copy the SERVICE_ID
  3. Email Templates → Create New Template
     → Set template like:
         Subject: New Portfolio Message from {{from_name}}
         Body:
           Name: {{from_name}}
           Email: {{from_email}}
           Service: {{service}}
           Message: {{message}}
     → Copy the TEMPLATE_ID
  4. Account → API Keys → copy your PUBLIC_KEY
  5. Paste all 3 below:
*/

const EMAILJS_SERVICE_ID  = 'service_jmbbnno';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'template_wcltkeo';  // e.g. 'template_xyz456'
const EMAILJS_PUBLIC_KEY  = 'rZRTo2VOGy6gCNdLb';   // e.g. 'abcDEF123456'

const contactLinks = [
  { label: 'Email',     value: 'vimalrajrip007@gmail.com',          href: 'mailto:vimalraj@email.com' },
  { label: 'WhatsApp',  value: '+91 9344771991',             href: 'https://wa.me/919344771991' },
  { label: 'LinkedIn',  value: '@vimalraj01',   href: 'www.linkedin.com/in/vimalraj01' },
  { label: 'Instagram', value: '@vimalselva6',               href: 'https://www.instagram.com/vimal_selva6' },
];

export default function Contact() {
  const { ref, controls, variants } = useReveal();
  const formRef = useRef(null);
  const [form, setForm]     = useState({ from_name: '', from_email: '', service: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.from_name || !form.from_email || !form.message) return;
    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { ...form },
        EMAILJS_PUBLIC_KEY
      );
      setStatus('sent');
      setForm({ from_name: '', from_email: '', service: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const fields = [
    { name: 'from_name',   label: 'Your Name',                 type: 'input'    },
    { name: 'from_email',  label: 'Email Address',             type: 'input'    },
    { name: 'service',     label: 'Service Needed',            type: 'input'    },
    { name: 'message',     label: 'Tell Me About Your Project',type: 'textarea' },
  ];

  return (
    <section id="contact" style={{ background: 'var(--deep)', padding: '130px 80px' }}>

      <motion.div ref={ref} initial="hidden" animate={controls} variants={variants} className="section-header">
        <span className="section-num">05</span>
        <div className="section-line" />
        <h2 className="section-title">Let's <em>Connect</em></h2>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '100px', alignItems: 'start' }} className="contact-grid">

        {/* Left */}
        <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.75, delay:0.1 }} viewport={{ once:true }}>
          <h3 style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'clamp(36px, 5vw, 60px)', fontWeight:300, lineHeight:1, marginBottom:'28px' }}>
            Have a project<br />in <em style={{ fontStyle:'italic', color:'#c9a96e' }}>mind?</em>
          </h3>
          <p style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'19px', fontWeight:300, color:'rgba(245,240,232,0.6)', lineHeight:1.75, marginBottom:'52px' }}>
            Whether it's a web app, ERP system, e-commerce store, server deployment, or digital marketing — I'm ready to deliver. Let's build something great together.
          </p>
          <div style={{ display:'flex', flexDirection:'column' }}>
            {contactLinks.map(cl => (
              <a key={cl.label} href={cl.href} target="_blank" rel="noreferrer" data-hover
                style={{ display:'flex', alignItems:'center', gap:'20px', padding:'18px 0', borderBottom:'1px solid var(--border)', color:'rgba(245,240,232,0.5)', fontSize:'13px', letterSpacing:'0.06em', transition:'color .3s' }}
                onMouseEnter={e => e.currentTarget.style.color='#c9a96e'}
                onMouseLeave={e => e.currentTarget.style.color='rgba(245,240,232,0.5)'}
              >
                <span style={{ fontSize:'9px', letterSpacing:'0.3em', textTransform:'uppercase', color:'#c9a96e', minWidth:'90px' }}>{cl.label}</span>
                {cl.value}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Right — Form */}
        <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} transition={{ duration:0.75, delay:0.25 }} viewport={{ once:true }}>

          {status === 'sent' ? (
            <div style={{ padding:'60px 40px', border:'1px solid rgba(201,169,110,0.3)', textAlign:'center' }}>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'52px', fontWeight:300, color:'#c9a96e', marginBottom:'16px' }}>✓</div>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'28px', fontWeight:300, marginBottom:'12px' }}>Message Sent!</div>
              <p style={{ fontSize:'13px', color:'rgba(245,240,232,0.5)', letterSpacing:'0.05em', marginBottom:'32px' }}>I'll get back to you shortly, Vimal will review your message.</p>
              <button onClick={() => setStatus('idle')} data-hover
                style={{ padding:'10px 32px', border:'1px solid rgba(201,169,110,0.3)', background:'transparent', color:'#c9a96e', fontSize:'10px', letterSpacing:'0.2em', textTransform:'uppercase', cursor:'none', fontFamily:"'Syne', sans-serif", transition:'all .3s' }}
                onMouseEnter={e => { e.currentTarget.style.background='#c9a96e'; e.currentTarget.style.color='#080808'; }}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#c9a96e'; }}
              >Send Another</button>
            </div>
          ) : (
            <div ref={formRef} style={{ display:'flex', flexDirection:'column' }}>
              {fields.map((field, i) => (
                <div key={field.name} style={{ position:'relative', borderBottom:'1px solid var(--border)', borderTop: i===0 ? '1px solid var(--border)' : 'none' }}>
                  <label style={{ position:'absolute', top:'18px', left:0, fontSize:'9px', letterSpacing:'0.3em', textTransform:'uppercase', color:'#c9a96e', pointerEvents:'none' }}>
                    {field.label}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea name={field.name} value={form[field.name]} onChange={handleChange} rows={4}
                      style={{ width:'100%', background:'transparent', border:'none', outline:'none', color:'var(--white)', fontFamily:"'Syne', sans-serif", fontSize:'14px', padding:'42px 0 16px', resize:'none' }}
                    />
                  ) : (
                    <input type={field.name==='from_email' ? 'email' : 'text'} name={field.name} value={form[field.name]} onChange={handleChange}
                      style={{ width:'100%', background:'transparent', border:'none', outline:'none', color:'var(--white)', fontFamily:"'Syne', sans-serif", fontSize:'14px', padding:'42px 0 16px' }}
                    />
                  )}
                </div>
              ))}

              {status === 'error' && (
                <p style={{ fontSize:'11px', color:'#e88a8a', letterSpacing:'0.1em', marginTop:'12px' }}>
                  Something went wrong. Please check your EmailJS config or try again.
                </p>
              )}

              <button onClick={handleSubmit} disabled={status==='sending'} data-hover
                style={{ marginTop:'32px', alignSelf:'flex-start', padding:'14px 52px', background:'transparent', border:'1px solid #c9a96e', color: status==='sending' ? 'rgba(201,169,110,0.4)' : '#c9a96e', fontSize:'11px', letterSpacing:'0.22em', textTransform:'uppercase', fontFamily:"'Syne', sans-serif", cursor:'none', transition:'all .3s' }}
                onMouseEnter={e => { if(status!=='sending'){ e.currentTarget.style.background='#c9a96e'; e.currentTarget.style.color='#080808'; }}}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color=status==='sending'?'rgba(201,169,110,0.4)':'#c9a96e'; }}
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          )}
        </motion.div>
      </div>

      <style>{`
        .contact-grid { grid-template-columns: 1fr 1fr; }
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 60px !important; }
          section#contact { padding: 90px 24px !important; }
        }
      `}</style>
    </section>
  );
}
